import type { LibrarySearchInput } from '../api';

export type DesktopSearchHistory = {
  kiipuSearchVisit?: string;
  kiipuSearchFocus?: boolean;
  kiipuSearchReader?: boolean;
};
export function desktopSearchHistory(value: unknown): DesktopSearchHistory {
  if (!record(value)) return {};
  return {
    ...(typeof value.kiipuSearchVisit === 'string' &&
    /^[a-z\d_-]{1,100}$/i.test(value.kiipuSearchVisit)
      ? { kiipuSearchVisit: value.kiipuSearchVisit }
      : {}),
    ...(value.kiipuSearchFocus === true ? { kiipuSearchFocus: true } : {}),
    ...(value.kiipuSearchReader === true ? { kiipuSearchReader: true } : {}),
  };
}

export type DesktopSearchFilters = Omit<LibrarySearchInput, 'q' | 'limit' | 'cursor'>;
export type DesktopSearchPosition = {
  top: number;
  pages: number;
  anchor?: string;
  offset?: number;
};
export type DesktopSearchVisit = {
  draft: string;
  input: { q: string; filters: DesktopSearchFilters } | null;
  filters: DesktopSearchFilters;
  position?: DesktopSearchPosition;
};

const prefix = 'kiipu:desktop-search:v1:';
const visits = new Map<string, DesktopSearchVisit>();
const recent = new Map<string, string[]>();
const last = new Map<string, string>();
const key = (owner: string, suffix: string) => `${prefix}${encodeURIComponent(owner)}:${suffix}`;
export const desktopSearchOwner = (origin: string, userId: string) =>
  JSON.stringify([origin, userId]);
export const searchQuery = (value: string) => value.trim().slice(0, 300);
export const canSearch = (value: string) => searchQuery(value).length >= 2;

function read(storageKey: string): unknown {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey) || 'null');
  } catch {
    return null;
  }
}
function write(storageKey: string, value: unknown) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Optional session storage; in-memory history still works.
  }
}
function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}
const uuid = /^[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12}$/i;
export function searchFilters(value: unknown): DesktopSearchFilters {
  if (!record(value)) return {};
  const result: DesktopSearchFilters = {};
  if (
    typeof value.folderId === 'string' &&
    (value.folderId === 'unfiled' || uuid.test(value.folderId))
  )
    result.folderId = value.folderId;
  if (typeof value.tagId === 'string' && uuid.test(value.tagId)) result.tagId = value.tagId;
  if (value.favorite === true) result.favorite = true;
  if (value.trash === true) result.trash = true;
  if (
    ['web', 'wechat', 'zhihu', 'xiaohongshu', 'weibo', 'x', 'bilibili', 'youtube'].includes(
      String(value.platform),
    )
  )
    result.platform = value.platform as DesktopSearchFilters['platform'];
  if (['article', 'post', 'video'].includes(String(value.kind)))
    result.kind = value.kind as DesktopSearchFilters['kind'];
  if (['complete', 'partial', 'link_only'].includes(String(value.quality)))
    result.quality = value.quality as DesktopSearchFilters['quality'];
  if (['queued', 'running', 'completed', 'failed'].includes(String(value.status)))
    result.status = value.status as DesktopSearchFilters['status'];
  return result;
}
function position(value: unknown): DesktopSearchPosition | undefined {
  if (!record(value) || !Number.isFinite(value.top) || !Number.isInteger(value.pages)) return;
  return {
    top: Math.max(0, Number(value.top)),
    pages: Math.max(1, Math.min(1000, Number(value.pages))),
    ...(typeof value.anchor === 'string' ? { anchor: value.anchor.slice(0, 100) } : {}),
    ...(Number.isFinite(value.offset) ? { offset: Number(value.offset) } : {}),
  };
}
export function readSearchVisit(owner: string, visitId: string): DesktopSearchVisit {
  const storageKey = key(owner, `visit:${visitId}`);
  if (visits.has(storageKey)) return visits.get(storageKey)!;
  const raw = read(storageKey);
  let value: DesktopSearchVisit = { draft: '', input: null, filters: {} };
  if (record(raw)) {
    const input =
      record(raw.input) && typeof raw.input.q === 'string' && canSearch(raw.input.q)
        ? { q: searchQuery(raw.input.q), filters: searchFilters(raw.input.filters) }
        : null;
    value = {
      draft: typeof raw.draft === 'string' ? raw.draft.slice(0, 300) : '',
      filters: searchFilters(raw.filters),
      input,
      position: position(raw.position),
    };
  }
  visits.set(storageKey, value);
  return value;
}
export function saveSearchVisit(
  owner: string,
  visitId: string,
  patch: Partial<DesktopSearchVisit>,
) {
  const storageKey = key(owner, `visit:${visitId}`);
  const value = { ...readSearchVisit(owner, visitId), ...patch };
  visits.set(storageKey, value);
  write(storageKey, value);
  last.set(owner, visitId);
  write(key(owner, 'last'), visitId);
}
export function lastSearchVisit(owner: string): string | null {
  const value = last.get(owner) ?? read(key(owner, 'last'));
  if (typeof value !== 'string' || !/^[a-z\d_-]{1,100}$/i.test(value)) return null;
  return visits.has(key(owner, `visit:${value}`)) || read(key(owner, `visit:${value}`)) !== null
    ? value
    : null;
}
export function createSearchVisit(owner: string, draft = '') {
  const id = crypto.randomUUID();
  saveSearchVisit(owner, id, { draft: draft.slice(0, 300), input: null, filters: {} });
  return id;
}
export function recentSearches(owner: string): string[] {
  if (recent.has(owner)) return recent.get(owner)!;
  const raw = read(key(owner, 'recent'));
  const value = Array.isArray(raw)
    ? [
        ...new Set(
          raw
            .filter((v): v is string => typeof v === 'string')
            .map(searchQuery)
            .filter(canSearch),
        ),
      ].slice(0, 5)
    : [];
  recent.set(owner, value);
  return value;
}
export function rememberSearch(owner: string, query: string) {
  const q = searchQuery(query);
  if (!canSearch(q)) return;
  const value = [q, ...recentSearches(owner).filter((entry) => entry !== q)].slice(0, 5);
  recent.set(owner, value);
  write(key(owner, 'recent'), value);
}
export function removeSearchHistory(owner: string, query?: string) {
  const value = query === undefined ? [] : recentSearches(owner).filter((entry) => entry !== query);
  recent.set(owner, value);
  write(key(owner, 'recent'), value);
}
export function clearDesktopSearchStorage() {
  visits.clear();
  recent.clear();
  last.clear();
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index--) {
      const storageKey = sessionStorage.key(index);
      if (storageKey?.startsWith(prefix)) sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Session storage can be disabled.
  }
}

export type PaletteChoice = { id: string; label: string; keywords?: string };
export function filterPaletteChoices<T extends PaletteChoice>(choices: T[], query: string): T[] {
  const words = searchQuery(query).toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return choices.filter((choice) =>
    words.every((word) =>
      `${choice.label} ${choice.keywords || ''}`.toLocaleLowerCase().includes(word),
    ),
  );
}
export function paletteNextIndex(current: number, direction: number, count: number) {
  return count ? (current + direction + count) % count : -1;
}
